import { SEO } from '@/components/seo'
import { Link } from 'react-router-dom'

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service - WealthSpace | Legal Terms"
        description="Terms of Service for WealthSpace - AI-powered software for financial advisers. Read our terms, conditions, and usage policies."
        url="/terms"
      />

      <section className="py-[80px] px-6 max-w-[900px] mx-auto text-center">
        <p className="text-sm font-semibold text-accent-teal tracking-[0.1em] uppercase mb-6 animate-fadeInUp opacity-0 [animation-delay:0.1s] [animation-fill-mode:forwards]">
          Legal
        </p>
        <h1 className="font-serif text-[clamp(36px,5vw,48px)] font-normal leading-[1.15] tracking-[-0.02em] text-brand-light mb-6 animate-fadeInUp opacity-0 [animation-delay:0.2s] [animation-fill-mode:forwards]">
          Terms of <span className="text-accent-teal">Service</span>
        </h1>
        <p className="text-lg text-brand-muted font-normal leading-[1.7] max-w-[600px] mx-auto animate-fadeInUp">
          Please read these terms carefully before using the WealthSpace platform.
        </p>
      </section>

      <section className="px-6 pb-[120px] max-w-[800px] mx-auto">
        <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-2xl p-12 max-sm:p-6">
          <p className="text-sm text-brand-muted/60 mb-10 pb-6 border-b border-white/[0.06]">
            Last updated: 3 January 2026
          </p>

          <div className="space-y-10">
            {/* Section 1: Acceptance of Terms */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">1.</span>Acceptance of Terms
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                By accessing or using the WealthSpace platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years of age and have the legal authority to enter into these Terms.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                WealthSpace Ltd (Company No. 15396707) ("Company", "we", "us", or "our") reserves the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Section 2: Description of Service */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">2.</span>Description of Service
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                WealthSpace provides AI-powered software designed for UK financial advisers, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2 mb-4">
                <li className="text-base leading-[1.8]">Meeting transcription and note generation</li>
                <li className="text-base leading-[1.8]">Suitability report generation and compliance documentation</li>
                <li className="text-base leading-[1.8]">Client relationship management tools</li>
                <li className="text-base leading-[1.8]">Document analysis and research capabilities</li>
                <li className="text-base leading-[1.8]">Workflow automation and task management</li>
                <li className="text-base leading-[1.8]">Integration with third-party financial services platforms</li>
              </ul>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                The Service is provided on a software-as-a-service (SaaS) basis. We may update, modify, or discontinue features of the Service at any time, with reasonable notice where practicable.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                The Service is intended as a tool to assist financial advisers in their work. It does not constitute financial advice, and users remain responsible for all advice given to their clients and for ensuring compliance with applicable regulations.
              </p>
            </section>

            {/* Section 3: User Accounts and Responsibilities */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">3.</span>User Accounts and Responsibilities
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                To access the Service, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2 mb-4">
                <li className="text-base leading-[1.8]">Providing accurate and complete registration information</li>
                <li className="text-base leading-[1.8]">Maintaining the security and confidentiality of your account credentials</li>
                <li className="text-base leading-[1.8]">All activities that occur under your account</li>
                <li className="text-base leading-[1.8]">Notifying us immediately of any unauthorised access or security breach</li>
                <li className="text-base leading-[1.8]">Ensuring that all users within your organisation comply with these Terms</li>
              </ul>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                You must not share your account credentials with any third party. Each user must have their own account. We reserve the right to suspend or terminate accounts that violate these requirements.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                If you are registering on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms.
              </p>
            </section>

            {/* Section 4: Acceptable Use */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">4.</span>Acceptable Use
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2 mb-4">
                <li className="text-base leading-[1.8]">Use the Service in any way that violates applicable laws or regulations, including the Financial Conduct Authority (FCA) rules and guidance</li>
                <li className="text-base leading-[1.8]">Upload, transmit, or store any content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable</li>
                <li className="text-base leading-[1.8]">Attempt to gain unauthorised access to any part of the Service or its related systems</li>
                <li className="text-base leading-[1.8]">Use the Service to transmit viruses, malware, or other malicious code</li>
                <li className="text-base leading-[1.8]">Interfere with or disrupt the integrity or performance of the Service</li>
                <li className="text-base leading-[1.8]">Attempt to reverse engineer, decompile, or disassemble any part of the Service</li>
                <li className="text-base leading-[1.8]">Use automated systems or software to extract data from the Service without our express permission</li>
                <li className="text-base leading-[1.8]">Resell, sublicense, or provide access to the Service to third parties without our written consent</li>
                <li className="text-base leading-[1.8]">Use the Service in a manner that could damage, disable, or impair the Service</li>
              </ul>
              <p className="text-base text-brand-muted leading-[1.8]">
                We reserve the right to investigate and take appropriate action against anyone who violates this provision, including removing content, suspending or terminating access, and reporting to law enforcement authorities.
              </p>
            </section>

            {/* Section 5: Intellectual Property */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">5.</span>Intellectual Property
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                The Service and its original content, features, and functionality are owned by WealthSpace Ltd (Company No. 15396707) and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Our Intellectual Property:</strong> All rights, title, and interest in and to the Service, including all software, algorithms, interfaces, documentation, and any improvements or modifications thereto, remain exclusively with us.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Your Content:</strong> You retain all rights to the data and content you upload to the Service ("Your Content"). By using the Service, you grant us a limited licence to use, process, and store Your Content solely for the purpose of providing the Service to you.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Generated Content:</strong> Content generated by the Service using your inputs ("Generated Content") may be used by you for your business purposes. However, you acknowledge that similar content may be generated for other users and we make no claim of exclusivity.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                <strong className="text-brand-light">Feedback:</strong> If you provide us with feedback, suggestions, or ideas, you grant us an unrestricted, perpetual, irrevocable licence to use such feedback for any purpose without compensation to you.
              </p>
            </section>

            {/* Section 6: Payment Terms */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">6.</span>Payment Terms
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Subscription Fees:</strong> Access to the Service requires payment of subscription fees as set out on our pricing page or in your order form. All fees are quoted in British Pounds (GBP) unless otherwise stated.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Billing:</strong> Subscription fees are billed in advance on a monthly or annual basis, depending on your chosen plan. You authorise us to charge your designated payment method for all applicable fees.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Free Trials:</strong> We may offer free trial periods. At the end of a trial, your subscription will automatically convert to a paid subscription unless you cancel before the trial ends.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Price Changes:</strong> We reserve the right to modify our pricing. Any price changes will be communicated to you at least 30 days before they take effect and will apply from your next billing period.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Taxes:</strong> All fees are exclusive of applicable taxes (including VAT), which will be added where required by law.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Refunds:</strong> Subscription fees are non-refundable except where required by law or as expressly stated in these Terms. Annual subscriptions may be eligible for a pro-rata refund if cancelled within the first 30 days.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                <strong className="text-brand-light">Late Payment:</strong> If payment is not received when due, we may suspend access to the Service until payment is made. We reserve the right to charge interest on overdue amounts at 4% above the Bank of England base rate.
              </p>
            </section>

            {/* Section 7: Data Protection and Privacy */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">7.</span>Data Protection and Privacy
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                Your use of the Service is also governed by our{' '}
                <Link to="/privacy-policy" className="text-accent-teal hover:text-accent-teal-light transition-colors">
                  Privacy Policy
                </Link>
                , which is incorporated into these Terms by reference.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                We take data protection seriously and have implemented appropriate technical and organisational measures to protect your data. Where we process personal data on your behalf, we act as a data processor and you act as the data controller under applicable data protection laws.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                You are responsible for ensuring that you have appropriate lawful bases for processing any personal data you upload to the Service and for providing appropriate notices to data subjects.
              </p>
            </section>

            {/* Section 8: Limitation of Liability */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">8.</span>Limitation of Liability
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Service Provided "As Is":</strong> The Service is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">No Guarantee:</strong> We do not warrant that the Service will be uninterrupted, error-free, secure, or free from viruses or other harmful components. We do not guarantee the accuracy, completeness, or usefulness of any content generated by the Service.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Limitation:</strong> To the maximum extent permitted by law, WealthSpace Ltd (Company No. 15396707) shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2 mb-4">
                <li className="text-base leading-[1.8]">Your use or inability to use the Service</li>
                <li className="text-base leading-[1.8]">Any unauthorised access to or use of our servers and/or any personal information stored therein</li>
                <li className="text-base leading-[1.8]">Any interruption or cessation of transmission to or from the Service</li>
                <li className="text-base leading-[1.8]">Any bugs, viruses, or similar that may be transmitted through the Service</li>
                <li className="text-base leading-[1.8]">Any errors or omissions in any content generated by the Service</li>
                <li className="text-base leading-[1.8]">Any decisions made or actions taken based on content generated by the Service</li>
              </ul>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Cap on Liability:</strong> Our total liability to you for all claims arising from or related to these Terms or the Service shall not exceed the greater of (a) the amounts paid by you to us in the twelve (12) months preceding the claim, or (b) one hundred pounds (GBP 100).
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                <strong className="text-brand-light">Exceptions:</strong> Nothing in these Terms excludes or limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited by English law.
              </p>
            </section>

            {/* Section 9: Indemnification */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">9.</span>Indemnification
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                You agree to indemnify, defend, and hold harmless WealthSpace Ltd (Company No. 15396707), its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2">
                <li className="text-base leading-[1.8]">Your use of the Service</li>
                <li className="text-base leading-[1.8]">Your violation of these Terms</li>
                <li className="text-base leading-[1.8]">Your violation of any third-party rights, including intellectual property or privacy rights</li>
                <li className="text-base leading-[1.8]">Any content you upload or transmit through the Service</li>
                <li className="text-base leading-[1.8]">Any advice you provide to your clients using the Service</li>
              </ul>
            </section>

            {/* Section 10: Termination */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">10.</span>Termination
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Termination by You:</strong> You may terminate your account at any time by contacting us or using the account cancellation feature in the Service. Termination will be effective at the end of your current billing period.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Termination by Us:</strong> We may suspend or terminate your access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms. We may also terminate the Service entirely with 90 days' notice.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Effect of Termination:</strong> Upon termination:
              </p>
              <ul className="list-disc pl-6 text-brand-muted space-y-2">
                <li className="text-base leading-[1.8]">Your right to use the Service will immediately cease</li>
                <li className="text-base leading-[1.8]">We will provide you with the ability to export your data for a period of 30 days following termination</li>
                <li className="text-base leading-[1.8]">After 30 days, we may delete your data in accordance with our data retention policies</li>
                <li className="text-base leading-[1.8]">Any provisions of these Terms that by their nature should survive termination shall survive</li>
              </ul>
            </section>

            {/* Section 11: Governing Law and Dispute Resolution */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">11.</span>Governing Law and Dispute Resolution
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                Any dispute arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                Before initiating any legal proceedings, you agree to first attempt to resolve any dispute informally by contacting us. We will endeavour to resolve any complaints within 30 days.
              </p>
            </section>

            {/* Section 12: General Provisions */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">12.</span>General Provisions
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and WealthSpace Ltd (Company No. 15396707) regarding the Service and supersede all prior agreements and understandings.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Severability:</strong> If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not be considered a waiver of such right or provision.
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">Assignment:</strong> You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                <strong className="text-brand-light">Force Majeure:</strong> We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, strikes, or shortages of transportation, facilities, fuel, energy, labour, or materials.
              </p>
            </section>

            {/* Section 13: Contact Information */}
            <section>
              <h2 className="font-serif text-2xl font-medium text-brand-light mb-4">
                <span className="text-accent-teal mr-2">13.</span>Contact Information
              </h2>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <p className="text-base text-brand-muted leading-[1.8] mb-4">
                <strong className="text-brand-light">WealthSpace Ltd (Company No. 15396707)</strong>
                <br />
                Email:{' '}
                <a
                  href="mailto:legal@wealthspace.co.uk"
                  className="text-accent-teal hover:text-accent-teal-light transition-colors"
                >
                  legal@wealthspace.co.uk
                </a>
                <br />
                Website:{' '}
                <a
                  href="https://wealthspace.co"
                  className="text-accent-teal hover:text-accent-teal-light transition-colors"
                >
                  wealthspace.co
                </a>
              </p>
              <p className="text-base text-brand-muted leading-[1.8]">
                WealthSpace Ltd (Company No. 15396707) is a company registered in England and Wales.
              </p>
            </section>
          </div>
        </div>
      </section>
    </>
  )
}
